import { Form } from '@ant-design/compatible';
import { EyeOutlined } from '@ant-design/icons';
import { Col } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PermissionCheck from 'common/features/permission-check';
import LockMessage from 'common/lock/components/lock-message';
import ActionButtons from 'common/navigation/components/action-buttons';
import Partners from 'common/partners/components/main-partners';
import PublishSteps from 'common/publish-steps/containers/publish-steps-container';
import Spinner from 'common/spinner/components/spinner';
import CreateOrders from 'order/create/containers/create-orders-container';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Outlet, Route, Routes } from 'react-router-dom';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import {
  Button,
  Div,
  DropdownButton,
  Flex,
  IconText,
  Info,
  notification,
  Row,
  SubTitle,
  Title,
  TitleActions,
} from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { severities } from 'utils/app-configs';
import { withRouter } from 'utils/withRouter';
import { referenceTypes } from '../../../utils/app-configs';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import Prompt from '../../../utils/prompt';
import Characteristics from './characteristics';
import Dates from './dates';
import General from './general';
import MeasuringPoints from './measuringPoints';
import Organisation from './organisation';
import TaskList from './task-list';


const ShowMoreButton = styled(Button)`
  margin: 10px 0;
`;

const EditButton = styled(Button)`
  i {
    margin-left: -1px;
    margin-right: 5px;
  }
`;

const StyledIconText = styled(IconText)`
  text-transform: uppercase;
`;

const EquipmentInfo = ({
  getEquipmentInfoRequest,
  saveEquipmentInfoRequest,
  resetSaveSuccess,
  id,
  data,
  error,
  form,
  isSaving,
  isLoading,
  saveSuccess,
  intl,
  renderMenu,
  lock,
  history,
  partners,
  userStatuses,
  partnerTypes,
  taskLists = [],
  visitTypes,
  getEquipmentInfoSuccess,
  renderConnectionStatus,
}) => {
  const [isEditable, setIsEditable] = useState(qs.parse(history.location.search).edit === 'true');
  const [charsAndMeasuringsPageSize, setCharsAndMeasuringsPageSize] = useState(10);
  const [charsAndMeasuringsShowMoreVisible, setCharsAndMeasuringsShowMoreVisible] = useState(true);
  const [selectedTaskList, setSelectedTaskList] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    getEquipmentInfoRequest({ id });
  }, []);

  useEffect(() => {
    if (!isSaving && saveSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'type.equipment.SAVE_SUCCESS' }),
        placement: 'top',
      });
      setIsEditable(false);
      resetSaveSuccess();
    }
  }, [saveSuccess, isSaving]);

  useDeepCompareEffect(() => {
    if (taskLists?.length && isObjectNullOrEmpty(selectedTaskList)) {
      setSelectedTaskList(taskLists[0]);
    }
  }, [taskLists, selectedTaskList]);

  const { getFieldValue } = form;
  const disabled = isSaving || isLoading || data.isInactive;
  const chars = getFieldValue('characteristics');
  const measurings = getFieldValue('measuringPoints');
  const measuringPoints = [...data?.measuringPoints
    ? data.measuringPoints
      ?.filter(m => m.docs && m.docs.length > 0)
      ?.map((m) => {
        // get the latest doc
        const [doc] = m.docs.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
        return {
          ...doc,
          type: m.name,
        };
      })
    : []];


  const setFormDirty = () => {
    setIsFormDirty(true);
  };

  const toggleEditState = () => {
    setIsEditable(!isEditable);
  };

  const resetForm = () => {
    form.resetFields();
    toggleEditState();
  };

  const save = () => {
    form.validateFields((err, values) => {
      if (!err) {
        // TODO remove isFormDirty
        if (form.isFieldsTouched() || isFormDirty) {
          saveEquipmentInfoRequest({ data, values });
        } else {
          resetForm();
        }
      }
    });
  };

  const showAllCharsAndMeasurings = () => {
    setCharsAndMeasuringsPageSize(Math.max(chars.length, measurings.length));
    setCharsAndMeasuringsShowMoreVisible(false);
  };

  const showLessCharsAndMeasurings = () => {
    setCharsAndMeasuringsPageSize(10);
    setCharsAndMeasuringsShowMoreVisible(true);
  };

  const handleVisitTypeChange = (visitTypeId) => {
    setSelectedTaskList(taskLists?.find(t => t?.id === visitTypeId));
  };

  const renderEditButton = () => {
    const editButtonIconClass = isSaving ?
      'fa-spinner fa-spin' : (lock && 'fa-lock') || 'fa-pencil';
    return (
      isEditable ?
        <DropdownButton
          disabled={!!lock}
          text={intl.formatMessage({ id: 'common.SAVE' })}
          iconClass={isSaving ? 'fa-spinner fa-spin' : 'fa-save'}
          onClick={save}
          subItems={[
            {
              text: intl.formatMessage({ id: 'common.CANCEL' }),
              onClick: resetForm,
            },
          ]}
        /> :
        <EditButton
          type='secondary'
          disabled={!!lock || isSaving}
          onClick={toggleEditState}
        >
          <i className={`fa ${editButtonIconClass}`} />
          {intl.formatMessage({ id: 'common.EDIT' })}
        </EditButton>
    );
  };

  form.getFieldDecorator('characteristics', {
    initialValue: data?.characteristics,
  });

  return (
    <Div marginBottom={20}>
      {renderMenu(
        <ActionButtons>
          {renderConnectionStatus && renderConnectionStatus()}
          {!data.isInactive ?
            <PermissionCheck permissionsList={['ordercreate']}>
              <Button
                type='secondary'
                onClick={() => history.push(`create-order/equipment/${id}`)}
              >
                <FormattedMessage id='common.CREATE_ORDER' />
              </Button>
            </PermissionCheck> :
            null
          }
          <TicketButton id={id} />
        </ActionButtons>,
      )}
      <ErrorHandler type='notification' error={error} />
      <PublishSteps
        id={id}
        reloadData={() => getEquipmentInfoRequest({ id })}
        onDataReloaded={getEquipmentInfoSuccess}
        type={referenceTypes.equipment}
      />
      {isLoading ?
        <Spinner title={intl.formatMessage({ id: 'common.LOADING_INFO' })} />
        :
        <Info>
          <Form layout='vertical'>
            <Row>
              <Col span={24}>
                <Flex>
                  <Title>{data.name}</Title>
                  {isObjectNullOrEmpty(error) ?
                    <PermissionCheck permissionsList={['installedbasemanage']}>
                      <TitleActions>
                        <LockMessage lock={lock} />
                        {data.isInactive ?
                          <StyledIconText
                            iconType='lock'
                            severity={severities.INACTIVE}
                            translationKey='equipment.info.INACTIVE'
                          /> :
                          renderEditButton()
                        }
                      </TitleActions>
                    </PermissionCheck> :
                    null}
                </Flex>
              </Col>
            </Row>
            <Row type='flex' gutter={25} marginBottom={20}>
              <Col sm={12} xs={24}>
                <SubTitle>{intl.formatMessage({ id: 'common.titles.GENERAL' })}</SubTitle>
                <General
                  data={data}
                  isEditable={isEditable}
                  form={form}
                  disabled={disabled}
                  setFormDirty={setFormDirty}
                  userStatuses={userStatuses}
                />
              </Col>
              <Col sm={12} xs={24}>
                <Organisation
                  data={data}
                  isEditable={isEditable}
                  form={form}
                  disabled={disabled}
                />
                <Dates data={data} isEditable={isEditable} form={form} disabled={disabled} />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Partners partners={partners} types={partnerTypes} canEdit={false} />
              </Col>
            </Row>
            <Row type='flex' gutter={25} marginBottom={25} marginTop={25}>
              <Col sm={8} xs={24}>
                <Characteristics
                  characteristics={form.getFieldValue('characteristics')}
                  onChange={v => form.setFieldsValue({
                    characteristics: v,
                  })}
                  isEditable={isEditable}
                  disabled={disabled}
                  setFormDirty={setFormDirty}
                  equipmentClass={data.class}
                  pageSize={charsAndMeasuringsPageSize}
                />
              </Col>
              <Col sm={16} xs={24}>
                <MeasuringPoints
                  measuringPoints={measuringPoints}
                  materialId={data.materialId}
                  form={form}
                  isEditable={isEditable}
                  disabled={disabled}
                  pageSize={charsAndMeasuringsPageSize}
                />
              </Col>
              <Col span={24}>
                <Flex jcc>
                  {chars && measurings && Math.max(chars.length, measurings.length) > 10 ?
                    <ShowMoreButton
                      icon={<EyeOutlined />}
                      onClick={
                        charsAndMeasuringsShowMoreVisible
                          ? showAllCharsAndMeasurings
                          : showLessCharsAndMeasurings
                      }
                    >
                      {charsAndMeasuringsShowMoreVisible
                        ? intl.formatMessage({ id: 'equipment.info.SHOW_ALL' })
                        : intl.formatMessage({ id: 'equipment.info.SHOW_LESS' })}
                    </ShowMoreButton>
                    : null}
                </Flex>
              </Col>
            </Row>
            <Row type='flex'>
              <Col span={24}>
                <TaskList
                  data={selectedTaskList}
                  visitTypes={visitTypes}
                  onVisitTypeChange={handleVisitTypeChange}
                />
              </Col>
            </Row>
          </Form>
        </Info>
      }
      <Routes>
        <Route path={'create-order/:type/:ids'} element={<CreateOrders />} />
      </Routes>
      <Outlet />
      {isEditable &&
        <Prompt
          when={form.isFieldsTouched || isFormDirty}
          message={intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })}
        />
      }
    </Div>
  );
};

EquipmentInfo.propTypes = {
  getEquipmentInfoRequest: PropTypes.func.isRequired,
  saveEquipmentInfoRequest: PropTypes.func.isRequired,
  resetSaveSuccess: PropTypes.func,
  id: PropTypes.string.isRequired,
  data: PropTypes.object,
  error: PropTypes.object,
  form: PropTypes.object,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  intl: PropTypes.object,
  renderMenu: PropTypes.func.isRequired,
  lock: PropTypes.object,
  history: PropTypes.object.isRequired,
  partners: PropTypes.array,
  userStatuses: PropTypes.array,
  partnerTypes: PropTypes.array,
  taskLists: PropTypes.array,
  visitTypes: PropTypes.array,
  getEquipmentInfoSuccess: PropTypes.func,
  renderConnectionStatus: PropTypes.func,
};

export default compose(
  Form.create(),
  withRouter,
  injectIntl,
)(EquipmentInfo);
